import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { ArrowLeft, Save, Send, Eye, Monitor, Smartphone, FlaskConical, ShieldCheck } from "lucide-react";
import { MKT, getAdminAuth } from "./AdminEmailMarketing";

const VARS = ["{{first_name}}", "{{company_name}}", "{{service_type}}", "{{city}}", "{{state}}"];

export default function CampaignEditor({ campaign, onClose }) {
  const [c, setC] = useState(campaign || {
    name: "", description: "", from_name: "", from_email: "", reply_to: "",
    subject: "", preview_text: "", html_content: "", list_ids: [], target_all: false, filters: {}, schedule_at: null,
  });
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState(null);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [testEmails, setTestEmails] = useState("");
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${MKT}/lists`, getAdminAuth()).then(r => setLists(r.data.lists)).catch(() => {});
    axios.get(`${MKT}/templates`, getAdminAuth()).then(r => setTemplates(r.data.templates)).catch(() => {});
    axios.get(`${MKT}/meta`, getAdminAuth()).then(r => setMeta(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setC(p => ({ ...p, [k]: e.target?.value ?? e }));

  const save = async (silent) => {
    setBusy(true);
    try {
      let saved = c;
      if (c.campaign_id) {
        await axios.put(`${MKT}/campaigns/${c.campaign_id}`, c, getAdminAuth());
      } else {
        const res = await axios.post(`${MKT}/campaigns`, c, getAdminAuth());
        saved = res.data;
        setC(saved);
      }
      if (!silent) toast.success("Campaign saved");
      setBusy(false);
      return saved;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
      setBusy(false);
      return null;
    }
  };

  const loadRecipientPreview = async () => {
    const saved = await save(true);
    if (!saved?.campaign_id) return;
    try {
      const res = await axios.get(`${MKT}/campaigns/${saved.campaign_id}/recipient-preview`, getAdminAuth());
      setRecipientPreview(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  const sendTest = async () => {
    const saved = await save(true);
    if (!saved?.campaign_id) return;
    try {
      const emails = testEmails.split(/[\s,;]+/).filter(Boolean);
      const res = await axios.post(`${MKT}/campaigns/${saved.campaign_id}/test-send`, { emails }, getAdminAuth());
      const ok = res.data.results.filter(r => r.success).length;
      const failed = res.data.results.filter(r => !r.success);
      if (ok) toast.success(`Test sent to ${ok} address(es)`);
      failed.forEach(f => toast.error(`${f.email}: ${f.error}`));
    } catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
  };

  const sendCampaign = async () => {
    const saved = await save(true);
    if (!saved?.campaign_id) return;
    setBusy(true);
    try {
      const res = await axios.post(`${MKT}/campaigns/${saved.campaign_id}/send`, { confirm: confirmText }, getAdminAuth());
      toast.success(res.data.status === "scheduled"
        ? `Campaign scheduled — ${res.data.queued} recipients queued`
        : `Campaign sending — ${res.data.queued} recipients queued`);
      setShowSendConfirm(false);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    setBusy(false);
  };

  const verifyBlocked = async () => {
    if (!c.campaign_id) return;
    setBusy(true);
    try {
      const res = await axios.post(`${MKT}/campaigns/${c.campaign_id}/verify-blocked`, {}, getAdminAuth());
      toast.success(`${res.data.queued} email(s) queued for verification — runs in the background. Re-run "Preview Recipients" in a few minutes.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to queue verification"); }
    setBusy(false);
  };

  const insertVar = (v) => setC(p => ({ ...p, html_content: (p.html_content || "") + " " + v }));
  const applyTemplate = (tid) => {
    const t = templates.find(x => x.template_id === tid);
    if (t) setC(p => ({ ...p, html_content: t.html_content, subject: p.subject || t.subject }));
  };

  const editable = !c.status || ["draft", "scheduled"].includes(c.status);

  return (
    <div className="space-y-4 mt-4" data-testid="campaign-editor">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="editor-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <h2 className="text-lg font-semibold text-foreground">{c.campaign_id ? c.name || "Edit Campaign" : "New Campaign"}</h2>
        {c.status && <Badge className="bg-slate-500/15 text-slate-400 text-[10px]">{c.status}</Badge>}
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => save(false)} disabled={busy || !editable} data-testid="save-campaign-btn"><Save className="w-4 h-4 mr-1" />Save Draft</Button>
        <Button size="sm" onClick={loadRecipientPreview} disabled={busy || (!c.list_ids?.length && !c.target_all)} data-testid="recipient-preview-btn"><Eye className="w-4 h-4 mr-1" />Preview Recipients</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: form */}
        <div className="space-y-3">
          <Card className="border-border"><CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Campaign name *</Label><Input value={c.name} onChange={set("name")} data-testid="campaign-name-input" /></div>
              <div className="col-span-2"><Label>Internal description</Label><Input value={c.description || ""} onChange={set("description")} /></div>
              <div><Label>From name</Label><Input value={c.from_name || ""} onChange={set("from_name")} placeholder="(uses Settings default)" /></div>
              <div><Label>From email</Label><Input value={c.from_email || ""} onChange={set("from_email")} placeholder="(uses Settings default)" /></div>
              <div><Label>Reply-to</Label><Input value={c.reply_to || ""} onChange={set("reply_to")} /></div>
              <div><Label>Schedule (optional)</Label>
                <Input type="datetime-local" value={c.schedule_at ? c.schedule_at.slice(0, 16) : ""}
                  onChange={(e) => setC(p => ({ ...p, schedule_at: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                  data-testid="campaign-schedule-input" />
              </div>
              <div className="col-span-2"><Label>Subject *</Label><Input value={c.subject || ""} onChange={set("subject")} data-testid="campaign-subject-input" /></div>
              <div className="col-span-2"><Label>Preview text</Label><Input value={c.preview_text || ""} onChange={set("preview_text")} /></div>
            </div>
          </CardContent></Card>

          <Card className="border-border"><CardContent className="p-4 space-y-3">
            <Label>Audience *</Label>
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="target-all-contacts"
                onClick={() => setC(p => ({ ...p, target_all: !p.target_all, list_ids: !p.target_all ? [] : p.list_ids }))}
                className={`px-2.5 py-1 rounded-full text-xs border font-medium transition-colors ${c.target_all ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
                All contacts ({meta?.totals?.contacts ?? "…"})
              </button>
              {lists.map(l => {
                const on = (c.list_ids || []).includes(l.list_id);
                return (
                  <button key={l.list_id} type="button" data-testid={`target-list-${l.name}`}
                    onClick={() => setC(p => ({ ...p, target_all: false, list_ids: on ? p.list_ids.filter(x => x !== l.list_id) : [...(p.list_ids || []), l.list_id] }))}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on && !c.target_all ? "bg-blue-500/20 border-blue-500/50 text-blue-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
                    {l.name} ({l.contact_count})
                  </button>
                );
              })}
              {lists.length === 0 && <p className="text-xs text-muted-foreground self-center">No lists yet — target all contacts, or create a list in the Lists tab.</p>}
            </div>
            {c.target_all && <p className="text-[10px] text-emerald-500">Targeting every non-archived contact (filters below still apply). Suppressed contacts are always excluded.</p>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Filter: country</Label>
                <Input value={c.filters?.country || ""} onChange={(e) => setC(p => ({ ...p, filters: { ...p.filters, country: e.target.value } }))} placeholder="e.g. US / CA" data-testid="filter-country-input" /></div>
              <div><Label className="text-xs">Filter: industry</Label>
                <Select value={c.filters?.industry || "any"} onValueChange={(v) => setC(p => ({ ...p, filters: { ...p.filters, industry: v === "any" ? "" : v } }))}>
                  <SelectTrigger data-testid="filter-industry-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="any">Any</SelectItem>{(meta?.industries || []).map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          </CardContent></Card>

          <Card className="border-border"><CardContent className="p-4 space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
              <Label>Email content (HTML) *</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Start from template:</span>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="w-56 h-9 text-sm border-blue-500/40 text-blue-500 font-medium" data-testid="apply-template-select"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
                  <SelectContent>{templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Textarea rows={12} className="font-mono text-xs" value={c.html_content || ""} onChange={set("html_content")} data-testid="campaign-html-input" />
            <div className="flex flex-wrap gap-1.5">
              {VARS.map(v => (
                <button key={v} type="button" onClick={() => insertVar(v)}
                  className="px-2 py-0.5 rounded text-[10px] font-mono text-blue-400 bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20">
                  {v}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">Fallbacks apply automatically (e.g. empty first_name → "there"). The unsubscribe footer + postal address are appended to every send.</p>
          </CardContent></Card>

          <Card className="border-border"><CardContent className="p-4 space-y-2">
            <Label className="flex items-center gap-1.5"><FlaskConical className="w-3.5 h-3.5" /> Send test email</Label>
            <div className="flex gap-2">
              <Input placeholder="you@fieldzai.com, other@…" value={testEmails} onChange={(e) => setTestEmails(e.target.value)} data-testid="test-emails-input" />
              <Button variant="outline" size="sm" onClick={sendTest} disabled={!testEmails.trim() || busy} data-testid="send-test-btn">Send Test</Button>
            </div>
            <p className="text-[10px] text-muted-foreground">Marked [TEST], renders personalization + footer, never affects analytics.</p>
          </CardContent></Card>
        </div>

        {/* Right: preview + recipients */}
        <div className="space-y-3">
          <Card className="border-border"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Preview</Label>
              <div className="flex gap-1">
                <Button variant={previewMode === "desktop" ? "default" : "ghost"} size="sm" onClick={() => setPreviewMode("desktop")} data-testid="preview-desktop-btn"><Monitor className="w-3.5 h-3.5" /></Button>
                <Button variant={previewMode === "mobile" ? "default" : "ghost"} size="sm" onClick={() => setPreviewMode("mobile")} data-testid="preview-mobile-btn"><Smartphone className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div className="border border-border rounded bg-slate-100 p-3 flex justify-center">
              <div className="bg-white text-black rounded shadow-sm p-4 overflow-y-auto"
                style={{ width: previewMode === "mobile" ? 375 : "100%", maxHeight: 420, fontSize: 14, fontFamily: "Arial, sans-serif" }}
                data-testid="email-preview-pane">
                <p className="text-xs text-gray-400 border-b pb-1.5 mb-2">Subject: {(c.subject || "").replace("{{first_name}}", "there").replace("{{company_name}}", "Sample Co")}</p>
                <div dangerouslySetInnerHTML={{
                  __html: (c.html_content || "<p style='color:#94a3b8'>Start writing…</p>")
                    .replaceAll("{{first_name}}", "Alex").replaceAll("{{company_name}}", "Sample Garage Doors")
                    .replaceAll("{{service_type}}", "Garage Door").replaceAll("{{city}}", "Austin").replaceAll("{{state}}", "TX")
                }} />
                <div className="mt-6 pt-3 border-t text-[11px] text-gray-400">
                  You're receiving this email because we believe Fieldz AI may be relevant to your business.<br />
                  <u>Unsubscribe</u> | <u>Manage Email Preferences</u><br />Fieldz AI · [postal address from Settings]
                </div>
              </div>
            </div>
          </CardContent></Card>

          {recipientPreview && (() => {
            const s = recipientPreview.summary;
            return (
              <Card className="border-blue-500/30 bg-blue-500/5" data-testid="recipient-preview-card">
                <CardContent className="p-4 space-y-2">
                  <p className="text-sm font-semibold text-foreground">Recipient Preview (backend-computed)</p>
                  <div className="text-xs space-y-0.5 text-muted-foreground">
                    <p>Targeted contacts: <b className="text-foreground">{s.targeted}</b></p>
                    <p className="font-medium text-foreground/80 pt-1">Excluded — suppression & data:</p>
                    <p className="pl-3">Unsubscribed: {s.unsubscribed} · Hard bounced: {s.hard_bounced} · Complaints: {s.complaints}</p>
                    <p className="pl-3">Manually suppressed: {s.manually_suppressed} · Invalid: {s.invalid} · Duplicates: {s.duplicates}</p>
                    <p className="font-medium text-foreground/80 pt-1">Excluded — email verification:</p>
                    <p className="pl-3" data-testid="verification-exclusions">Unverified: {s.unverified || 0} · Verifying: {s.verifying || 0} · Invalid result: {s.verification_invalid || 0} · Expired: {s.verification_expired || 0}</p>
                    <p className="pl-3">Risky blocked: {s.risky_blocked || 0} · Catch-all blocked: {s.catch_all_blocked || 0} · Unknown blocked: {s.unknown_blocked || 0}</p>
                  </div>
                  <div className="rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wide">Final sendable recipients</span>
                    <span className="text-xl font-bold text-emerald-500 tabular-nums" data-testid="eligible-count">{s.eligible}</span>
                  </div>
                  {(s.verifiable_blocked || 0) > 0 && (
                    <div className="rounded border border-amber-500/30 bg-amber-500/10 p-2.5 space-y-1.5" data-testid="verifiable-blocked-box">
                      <p className="text-[11px] text-amber-600">
                        <b>{s.verifiable_blocked}</b> contact(s) are excluded only because they are unverified or their result expired. Verify them, then re-run the preview.
                      </p>
                      <Button size="sm" variant="outline" className="h-7 border-amber-500/50 text-amber-600 hover:text-amber-500"
                        onClick={verifyBlocked} disabled={busy} data-testid="verify-blocked-btn">
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" />Verify {s.verifiable_blocked} blocked email(s)
                      </Button>
                    </div>
                  )}
                  <div className="max-h-32 overflow-y-auto border border-border rounded">
                    {recipientPreview.sample.map((r, i) => (
                      <div key={i} className="px-2 py-1 text-[11px] border-b border-border/30 flex justify-between">
                        <span className="text-foreground">{r.email}</span>
                        <span className="text-muted-foreground">{r.company_name || r.industry || ""}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="w-full" disabled={!s.eligible || busy || !editable}
                    onClick={() => { setConfirmText(""); setShowSendConfirm(true); }} data-testid="open-send-confirm-btn">
                    <Send className="w-4 h-4 mr-1" />
                    {c.schedule_at ? "Schedule Campaign" : "Send Campaign"} ({s.eligible} recipients)
                  </Button>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      </div>

      {/* Send confirmation */}
      <Dialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
        <DialogContent className="max-w-md" data-testid="send-confirm-dialog">
          <DialogHeader><DialogTitle>Confirm Campaign Send</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            You are about to {c.schedule_at ? "schedule" : "send"} this marketing campaign to{" "}
            <b className="text-foreground">{recipientPreview?.summary?.eligible} verified, eligible recipients</b>
            {recipientPreview?.summary ? ` (${Math.max((recipientPreview.summary.targeted || 0) - (recipientPreview.summary.eligible || 0), 0)} excluded by suppression or verification rules)` : ""}.
            Every email passes a final suppression + verification check immediately before it is sent.
          </p>
          <div>
            <Label>Type <b>SEND</b> to confirm</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="SEND" data-testid="send-confirm-input" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowSendConfirm(false)}>Cancel</Button>
            <Button disabled={confirmText.trim().toUpperCase() !== "SEND" || busy} onClick={sendCampaign} data-testid="confirm-send-btn">
              <Send className="w-4 h-4 mr-1" />Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
